import { useDispatch, useSelector } from "react-redux";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { actions as sharedActions } from "@shared/store";
import { useLoader } from "@shared/hooks";
import { actions as RecognitionActions, selectors as RecognitionSelectors } from "@containers/Recognition/store";
import { actions as SparksActions, selectors as SparksSelectors } from "@containers/Recognition/store/sparks";
import { CreateRecognitionDto } from "@containers/Recognition/interfaces";
import { RecognitionForm } from "@containers/Recognition/components";
import { OVERLAY_TYPES } from "@shared/constants";
import { Option, OverlaySize } from "@shared/interfaces";

import { Tooltip } from "../../../../shared/components/Tooltip";

import "./index.scss";

interface RecognitionCreateContainerProps {
  onClose: () => void;
  // 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: optional prefill for the "Recognise" nudge from a Leaderboard
  // card -- built by the caller (SparkSpreadSection) from the row with no extra fetch, matching
  // formHelpers.tsx's own handlers.user.prepareOptionFunction shape. OPTIONAL, and every existing call
  // site (AllRecognitionsContainer/RecognitionsTabBarContainer/AllBadgesContainer's own
  // openRecognitionFormOverlay) must keep opening a blank form exactly as before -- none of them pass
  // this prop.
  recipient?: Option;
}

const RecognitionCreateContainer: FC<RecognitionCreateContainerProps> = (props) => {
  const { onClose, recipient } = props;

  const dispatch = useDispatch();

  // 923-core-context context/engineering/spark-rewards/phase-54-give-recognition-drawer.md: captured ONCE per mount, matching MySparksContainer's own
  // tzOffsetMinutes precedent -- this container owns the giving-activity fetch that used to belong to
  // whichever screen was already mounted behind the overlay; a fresh drawer instance always refetches
  // its own copy rather than assuming one is already in the sparks slice.
  const [tzOffsetMinutes] = useState(() => new Date().getTimezoneOffset());

  const coreValues = useSelector(RecognitionSelectors.getCoreValues());
  const givingActivity = useSelector(SparksSelectors.getGivingActivity());
  // "To" section empty-state chips (pavel's Figma empty-state note, frame 9198:1162) -- the SAME
  // viewer-scoped list SparkSpreadSection already renders as full cards on the Leaderboard tab, fetched
  // again here rather than assumed already-present in the sparks slice: a drawer opened without ever
  // visiting that tab must still see its own copy.
  const suggestedCollaborators = useSelector(SparksSelectors.getLeaderboardCollaborators());
  const suggestedCollaboratorsIsLoaded = useSelector(SparksSelectors.getLeaderboardCollaboratorsIsLoaded());

  // 923-core-context context/engineering/spark-rewards/phase-56-drawer-follow-ups.md #1: keep the drawer open until the api actually succeeds, so a refusal
  // (e.g. the per-pair cooldown) doesn't drop what the giver typed -- errorHandlerMiddleware already
  // toasts the refusal reason, and the success `cb` alone replaces this drawer with the note overlay.
  // Precedent copied exactly from DenyRedemptionRequestContainer: a redux-derived `isSubmitting` alone
  // can't stop a same-tick double submit (it only reflects a NEW render), so a synchronous ref guards the
  // submit handler itself and is reset once the loader clears without a success.
  const { isLoading: isSubmitting } = useLoader({
    name: "CreateRecognition",
    actionTypes: [RecognitionActions.createRecognition],
  });
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!isSubmitting) {
      isSubmittingRef.current = false;
    }
  }, [isSubmitting]);

  // A click on the backdrop closes this drawer exactly like the ✕ below (every
  // `showOverlay({ type: CREATE_RECOGNITION, ... })` call site opens it with `closeOnOverlayClick: true`
  // -- verified: AllRecognitionsContainer/AllBadgesContainer/RecognitionsTabBarContainer/
  // LeaderboardsContainer/SparkSpreadSection all pass that literal), so it needs the same in-flight guard
  // as the ✕ itself. `updateOverlay` already merges a `Partial<OverlayComponent>` onto the live overlay
  // (reducer.tsx `updateOverlay` handler), and `closeOnOverlayClick` is a plain field on that same
  // interface -- no shared-code change needed. Restoring unconditionally to `true` (not a saved prior
  // value) is safe because every call site above opens this overlay with that same literal.
  useEffect(() => {
    dispatch(sharedActions.updateOverlay({ closeOnOverlayClick: !isSubmitting }));
  }, [dispatch, isSubmitting]);

  useEffect(() => {
    dispatch(RecognitionActions.getCoreValues.request());
    dispatch(SparksActions.getGivingActivity.request(tzOffsetMinutes));
    dispatch(SparksActions.getLeaderboardCollaborators.request());
  }, [dispatch, tzOffsetMinutes]);

  const changeFormValues = useCallback(() => {
    dispatch(sharedActions.updateOverlay({ leaveConfirm: true }));
  }, [dispatch]);

  const openRecognitionCreationNoteOverlay = useCallback(() => {
    dispatch(
      sharedActions.showOverlay({
        type: OVERLAY_TYPES.SHOW_RECOGNITION_CREATION_NOTE,
        size: OverlaySize.EXTRA_REDUCED,
        closeOnOverlayClick: false,
        props: {},
      }),
    );
  }, [dispatch]);

  const handleSubmitForm = useCallback(
    (payload: CreateRecognitionDto) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      dispatch(RecognitionActions.createRecognition.request({ ...payload, cb: openRecognitionCreationNoteOverlay }));
    },
    [dispatch, openRecognitionCreationNoteOverlay],
  );

  return (
    <div className="recognition-drawer">
      <div className="recognition-drawer-header">
        <span className="recognition-drawer-title">New recognition</span>
        <Tooltip text="Close">
          <button
            type="button"
            className="recognition-drawer-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isSubmitting}
          >
            ✕
          </button>
        </Tooltip>
      </div>
      <RecognitionForm
        onChangeForm={changeFormValues}
        onSubmitForm={handleSubmitForm}
        initialUser={recipient}
        coreValues={coreValues}
        givingActivity={givingActivity}
        suggestedCollaborators={suggestedCollaborators}
        suggestedCollaboratorsIsLoaded={suggestedCollaboratorsIsLoaded}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default RecognitionCreateContainer;
